/* oxlint-disable react/refs -- react-dnd connects drag and drop through a DOM ref */
import { DotsSixVerticalIcon, PencilSimpleIcon } from '@phosphor-icons/react'
import { type ReactElement, useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { openListRuleEditor } from 'app/components/lists/filter/openListRuleEditor'
import { BuiltInListMark } from 'app/components/lists/BuiltInListMark'
import { ListVisibilityTag } from 'app/components/lists/ListVisibilityTag'
import { OverflowText } from 'app/components/shared/styled/OverflowText'
import { Button } from 'app/components/ui/button'
import { Switch } from 'app/components/ui/switch'
import { useOpenNavListEditor } from 'app/hooks/useOpenNavListEditor'
import { cn } from 'app/lib/utils'
import type { NavListEntry, NavListGroup } from 'app/types/navigation'
import { NAV_LIST_GROUP } from 'lib/navLists'
import { navListVisibility } from 'lib/navLists'
import { RECORD_TYPES } from 'lib/recordTypes'

const NAV_LIST_DRAG_TYPE = `nav-list`
type ListDragItem = { key: string; group: NavListGroup }

export function DraggableListRow({
  entry,
  visible,
  onMove,
  onToggleVisible,
  onListsChanged,
  onDeleted,
}: {
  entry: NavListEntry
  visible: boolean
  onMove: (draggedKey: string, targetKey: string) => void
  onToggleVisible: () => void
  onListsChanged: () => void
  onDeleted: () => void
}): ReactElement {
  const ref = useRef<HTMLDivElement>(null)
  const openNavListEditor = useOpenNavListEditor()
  const group = NAV_LIST_GROUP[entry.source]
  const [, dropRef] = useDrop<ListDragItem>({
    accept: NAV_LIST_DRAG_TYPE,
    hover: (dragged) => {
      // A list only trades places within its own group: a practice list never lands among the built-in ones.
      if (dragged.key !== entry.key && dragged.group === group) onMove(dragged.key, entry.key)
    },
  })
  const [{ isDragging }, dragRef] = useDrag({
    type: NAV_LIST_DRAG_TYPE,
    item: { key: entry.key, group } satisfies ListDragItem,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  })
  dragRef(dropRef(ref))
  const Icon = RECORD_TYPES[entry.recordType].icon

  return (
    <div
      ref={ref}
      className={cn(
        `flex h-10 cursor-grab items-center gap-2 rounded-md px-2 hover:bg-(--fg-strong)/4`,
        isDragging && `opacity-40`,
      )}
    >
      <DotsSixVerticalIcon size={16} weight="bold" className="shrink-0 text-minimal" aria-hidden="true" />
      {entry.source === `system` ? (
        <BuiltInListMark>
          <Icon />
        </BuiltInListMark>
      ) : (
        <Icon />
      )}
      <OverflowText
        hasTooltip
        content={entry.item.name}
        className={cn(`min-w-0 grow cursor-grab! text-body-sm`, visible ? `text-strong` : `text-minimal`)}
      />
      <ListVisibilityTag visibility={navListVisibility(entry)} />
      {(entry.customPage || (entry.list && entry.source === `custom`)) && (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Edit ${entry.item.name}`}
          onClick={() =>
            entry.list && !entry.customPage
              ? openListRuleEditor({ list: entry.list, onSaved: onListsChanged, onDeleted })
              : openNavListEditor(entry, { onSaved: onListsChanged, onDeleted })
          }
        >
          <PencilSimpleIcon size={14} weight="bold" className="text-subtle" />
        </Button>
      )}
      <Switch
        checked={visible}
        onCheckedChange={onToggleVisible}
        aria-label={`Show ${entry.item.name} in the sidebar`}
      />
    </div>
  )
}
